import {
  ChatAvatars,
  WorkThreadAvatar,
} from "@app/components/assistant/conversation/ChatAvatars";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useSearchPodConversations } from "@app/hooks/conversations/useSearchPodConversations";
import { useSearchPrivateConversations } from "@app/hooks/conversations/useSearchPrivateConversations";
import {
  useMarkChatRead,
  useRenameChat,
  useSetChatPinned,
} from "@app/lib/swr/chats";
import { formatActivityDate } from "@app/lib/utils/activity_date";
import { getChatRoute, getConversationRoute } from "@app/lib/utils/router";
import { type ChatSummaryType, chatUnreadCap } from "@app/types/api/chats";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  CheckDouble,
  Clock,
  cn,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Edit04,
  Icon,
  Input,
  LoadingBlock,
  MessageChatSquare,
  NavigationListItem,
  NavigationListItemAction,
  Pin01,
  Plus,
  Spinner,
} from "@dust-tt/sparkle";
import { useContext, useMemo, useRef, useState } from "react";

const sectionLabelClassName =
  "flex h-8 items-center px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground";

export const chatRowClassName =
  "[&_.peer\/menu-button]:gap-3 [&_.peer\/menu-button]:px-2.5 [&_.peer\/menu-button]:py-2.5 [&_.peer\/menu-button]:text-sm [&_.peer\/menu-button]:text-primary [&_.peer\/menu-button_div[title]]:text-[13px] [&_.peer\/menu-button_div[title]]:leading-[18px]";

export const chatRowEnterClassName =
  "animate-in fade-in slide-in-from-top-2 duration-300 ease-out motion-reduce:animate-none";

// Friday is dropped from a chat's participant key, so a thread holding only
// the viewer and Friday is a work thread split out of the Friday DM.
export function isWorkThread(
  chat: Pick<ChatSummaryType, "kind" | "participantIds">
): boolean {
  return chat.kind === "topic" && chat.participantIds.length === 0;
}

export function ChatRowSuffix({
  activityAt,
  unreadCount,
  unreadCap,
  name,
}: {
  activityAt: number;
  unreadCount: number;
  unreadCap?: number;
  name: string;
}) {
  return (
    <span className="flex items-center gap-2">
      <span className="text-xs font-normal text-muted-foreground">
        {formatActivityDate(activityAt)}
      </span>
      <UnreadBadge
        unreadCount={unreadCount}
        unreadCap={unreadCap}
        name={name}
      />
    </span>
  );
}

export function UnreadBadge({
  unreadCount,
  unreadCap,
  name,
}: {
  unreadCount: number;
  unreadCap?: number;
  name: string;
}) {
  if (unreadCount <= 0) {
    return null;
  }
  return (
    <span
      role="img"
      aria-label={`${unreadCount} unread in ${name}`}
      className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[#e5484d] px-1 text-[10px] font-semibold leading-none text-white"
    >
      {unreadCount}
      {unreadCap !== undefined && unreadCount >= unreadCap && "+"}
    </span>
  );
}

function chatName(chat: ChatSummaryType): string {
  return chat.title ?? chat.label;
}

function RenameChatDialog({
  chat,
  owner,
  onClose,
}: {
  chat: ChatSummaryType;
  owner: LightWorkspaceType;
  onClose: () => void;
}) {
  const renameChat = useRenameChat({ owner });
  const [title, setTitle] = useState(chatName(chat));

  const save = async () => {
    const trimmed = title.trim();
    if (trimmed !== "") {
      await renameChat(chat.sId, trimmed);
    }
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename chat</DialogTitle>
        </DialogHeader>
        <DialogContainer>
          <Input
            placeholder="Enter new title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void save();
              }
            }}
          />
        </DialogContainer>
        <DialogFooter
          rightButtonProps={{
            label: "Save",
            variant: "primary",
            onClick: save,
          }}
          leftButtonProps={{
            label: "Cancel",
            variant: "outline",
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

type ConversationSearchResult = ConversationWithoutContentType & {
  spaceName: string | null;
};

function conversationSearchResultHref(
  owner: LightWorkspaceType,
  conversation: ConversationSearchResult
): string {
  return conversation.chatId
    ? getChatRoute(owner.sId, conversation.chatId, conversation.sId)
    : getConversationRoute(owner.sId, conversation.sId);
}

interface ConversationSearchResultsProps {
  owner: LightWorkspaceType;
  query: string;
  shownChatIds: ReadonlySet<string>;
  hasLocalMatches: boolean;
  onNavigate: () => Promise<void>;
}

// Server matches for the sidebar search, beyond the chats already listed by
// name: private conversations by title, Pod conversations by message content.
function ConversationSearchResults({
  owner,
  query,
  shownChatIds,
  hasLocalMatches,
  onNavigate,
}: ConversationSearchResultsProps) {
  const privateSearch = useSearchPrivateConversations({
    workspaceId: owner.sId,
    query,
  });
  const podSearch = useSearchPodConversations({
    workspaceId: owner.sId,
    query,
    limit: 20,
  });

  const results = useMemo(() => {
    const seenIds = new Set<string>();
    const candidates: ConversationSearchResult[] = [
      ...podSearch.conversations,
      ...privateSearch.conversations.map((conversation) => ({
        ...conversation,
        spaceName: null,
      })),
    ];
    return candidates.filter((conversation) => {
      if (
        seenIds.has(conversation.sId) ||
        (conversation.chatId !== null && shownChatIds.has(conversation.chatId))
      ) {
        return false;
      }
      seenIds.add(conversation.sId);
      return true;
    });
  }, [podSearch.conversations, privateSearch.conversations, shownChatIds]);

  if (results.length === 0) {
    if (hasLocalMatches) {
      return null;
    }
    if (privateSearch.isSearching || podSearch.isSearching) {
      return (
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
          <Spinner size="xs" />
          Searching conversations
        </div>
      );
    }
    return (
      <div className="px-3 py-2 text-sm text-muted-foreground">
        No chats match your search
      </div>
    );
  }

  return (
    <>
      <div className={sectionLabelClassName}>Conversations</div>
      {results.map((conversation) => (
        <NavigationListItem
          key={conversation.sId}
          label={conversation.title ?? "Untitled conversation"}
          description={conversation.spaceName ?? undefined}
          icon={MessageChatSquare}
          href={conversationSearchResultHref(owner, conversation)}
          shallow
          className={chatRowClassName}
          onClick={onNavigate}
        />
      ))}
    </>
  );
}

interface ChatsListProps {
  owner: LightWorkspaceType;
  pinned: ChatSummaryType[];
  recent: ChatSummaryType[];
  isLoading: boolean;
  titleFilter: string;
  activeChatId: string | null;
}

export function ChatsList({
  owner,
  pinned,
  recent,
  isLoading,
  titleFilter,
  activeChatId,
}: ChatsListProps) {
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
  const setChatPinned = useSetChatPinned({ owner });
  const markChatRead = useMarkChatRead({ owner });
  const [renamingChat, setRenamingChat] = useState<ChatSummaryType | null>(
    null
  );
  const seenChatIdsRef = useRef<Set<string> | null>(null);
  const enteringChatIdsRef = useRef(new Set<string>());

  const needle = titleFilter.trim().toLowerCase();
  const matchesFilter = (chat: ChatSummaryType) =>
    needle === "" ||
    chat.label.toLowerCase().includes(needle) ||
    (chat.title ?? "").toLowerCase().includes(needle);
  const visiblePinned = pinned.filter(matchesFilter);
  const visibleRecent = recent.filter(matchesFilter);
  const shownChatIds = new Set(
    [...visiblePinned, ...visibleRecent].map((chat) => chat.sId)
  );

  const closeSidebarBeforeNavigating = async () => {
    if (sidebarOpen) {
      setSidebarOpen(false);
      await new Promise((resolve) => setTimeout(resolve, 600));
    }
  };

  const searchResults = needle ? (
    <ConversationSearchResults
      owner={owner}
      query={titleFilter.trim()}
      shownChatIds={shownChatIds}
      hasLocalMatches={visiblePinned.length + visibleRecent.length > 0}
      onNavigate={closeSidebarBeforeNavigating}
    />
  ) : null;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-3 py-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <LoadingBlock key={index} className="h-10 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  // Only chats that appear after the first load animate in; the initial list
  // and re-sorts stay still.
  const allChatIds = [...pinned, ...recent].map((chat) => chat.sId);
  if (seenChatIdsRef.current === null) {
    seenChatIdsRef.current = new Set(allChatIds);
  }
  for (const sId of allChatIds) {
    if (!seenChatIdsRef.current.has(sId)) {
      seenChatIdsRef.current.add(sId);
      enteringChatIdsRef.current.add(sId);
    }
  }

  if (visiblePinned.length === 0 && visibleRecent.length === 0) {
    return needle ? (
      <div className="sm:flex sm:flex-col sm:gap-0.5">{searchResults}</div>
    ) : (
      <div className="flex flex-col items-start gap-2 px-3 py-2 text-sm text-muted-foreground">
        No chats yet
        <Button
          label="New chat"
          icon={Plus}
          variant="outline"
          size="xs"
          href={getConversationRoute(owner.sId)}
          onClick={() => setSidebarOpen(false)}
        />
      </div>
    );
  }

  const renderRow = (chat: ChatSummaryType) => {
    const name = chatName(chat);
    const isUnread = chat.unreadCount > 0;
    return (
      <NavigationListItem
        key={chat.sId}
        selected={chat.sId === activeChatId}
        label={name}
        description={
          chat.preview
            ? `${chat.preview.senderName}: ${chat.preview.text}`
            : undefined
        }
        href={getChatRoute(owner.sId, chat.sId)}
        shallow
        hasActivity={isUnread}
        suffix={
          <ChatRowSuffix
            activityAt={chat.lastActivityAt}
            unreadCount={chat.unreadCount}
            unreadCap={chatUnreadCap}
            name={name}
          />
        }
        className={cn(
          chatRowClassName,
          enteringChatIdsRef.current.has(chat.sId) && chatRowEnterClassName
        )}
        avatar={
          <div className="relative shrink-0">
            {isWorkThread(chat) ? (
              <WorkThreadAvatar seed={chat.sId} name={name} />
            ) : (
              <ChatAvatars participants={chat.participants} />
            )}
            {chat.kind === "routine" && (
              <Icon
                visual={Clock}
                size="xs"
                className="absolute -bottom-1 -right-1 rounded-full bg-background text-muted-foreground ring-2 ring-app-background"
              />
            )}
          </div>
        }
        moreMenu={
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <NavigationListItemAction />
            </DropdownMenuTrigger>
            <DropdownMenuContent onFocusOutside={(e) => e.preventDefault()}>
              <DropdownMenuItem
                label={chat.pinned ? "Unpin" : "Pin"}
                icon={Pin01}
                onClick={() => void setChatPinned(chat.sId, !chat.pinned)}
              />
              {chat.kind !== "direct" && chat.kind !== "support" && (
                <DropdownMenuItem
                  label="Rename"
                  icon={Edit04}
                  onClick={() => setRenamingChat(chat)}
                />
              )}
              {isUnread && (
                <DropdownMenuItem
                  label="Mark as read"
                  icon={CheckDouble}
                  onClick={() => void markChatRead(chat.sId)}
                />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        }
        onClick={closeSidebarBeforeNavigating}
      />
    );
  };

  return (
    <div className="sm:flex sm:flex-col sm:gap-0.5">
      {renamingChat && (
        <RenameChatDialog
          key={renamingChat.sId}
          chat={renamingChat}
          owner={owner}
          onClose={() => setRenamingChat(null)}
        />
      )}
      {visiblePinned.length > 0 && (
        <>
          <div className={sectionLabelClassName}>Pinned</div>
          {visiblePinned.map(renderRow)}
        </>
      )}
      {visibleRecent.length > 0 && (
        <>
          {visiblePinned.length > 0 && (
            <div className={sectionLabelClassName}>Recent</div>
          )}
          {visibleRecent.map(renderRow)}
        </>
      )}
      {searchResults}
    </div>
  );
}
